/**
 * color-math.ts — pure color primitives. Zero runtime dependency.
 *
 * Hand-rolled: sRGB ↔ Lab, sRGB ↔ OKLab, ΔE (CIE76), WCAG relative-luminance +
 * contrast ratio, and `perceivedLuminance` (the 0.299/0.587/0.114 weighting the
 * buyer storefront uses to pick button text).
 *
 * Lives in `@a2z/ui` (source-consumed, no build step) because BOTH apps need it:
 * the SELLER's logo brand-color engine (`onboarding/wizard/brand-colors`, its
 * original home) and the BUYER's per-section surface derivation
 * (`StorefrontThemeStyle.buildSurfaceVars`). Import as
 * `@a2z/ui/color/color-math`.
 *
 * Its unit tests deliberately stay in the seller
 * (`brand-colors/color-math.test.ts`, vitest) rather than being ported to this
 * package's `tsx --test` script — moving code should not cost coverage.
 *
 * All conversions round-trip; unit tests pin known reference values.
 */

export interface Rgb {
  /** 0–255 */ r: number;
  /** 0–255 */ g: number;
  /** 0–255 */ b: number;
}

export interface Lab {
  /** 0–100 */ L: number;
  a: number;
  b: number;
}

export interface OkLab {
  /** 0–1 */ L: number;
  a: number;
  b: number;
}

// ─── hex parse / format ──────────────────────────────────────────────────────

/** Parse `#rgb` / `#rrggbb` (ignoring any alpha bytes) into 0–255 channels, else null. */
export function parseHex(input: string): Rgb | null {
  const safe = (input ?? "").trim();
  const m = /^#([0-9a-fA-F]{3,4}|[0-9a-fA-F]{6}|[0-9a-fA-F]{8})$/.exec(safe);
  if (!m) return null;
  let hex = m[1];
  if (hex.length === 3 || hex.length === 4) {
    hex = hex
      .slice(0, 3)
      .split("")
      .map((c) => c + c)
      .join("");
  } else {
    hex = hex.slice(0, 6);
  }
  const n = parseInt(hex, 16);
  return { r: (n >> 16) & 255, g: (n >> 8) & 255, b: n & 255 };
}

function clamp255(v: number): number {
  return Math.max(0, Math.min(255, Math.round(v)));
}

/** Format 0–255 channels as `#rrggbb` (uppercase). */
export function formatHex({ r, g, b }: Rgb): string {
  const h = (v: number) => clamp255(v).toString(16).padStart(2, "0");
  return `#${h(r)}${h(g)}${h(b)}`.toUpperCase();
}

/** True when the string is a valid `#rgb`/`#rrggbb` (or alpha variants) hex color. */
export function isValidHex(input: string): boolean {
  return parseHex(input) !== null;
}

// ─── sRGB linearization ──────────────────────────────────────────────────────

/** sRGB 0–255 channel → linear-light 0–1. */
function srgbToLinear(c: number): number {
  const cs = c / 255;
  return cs <= 0.04045 ? cs / 12.92 : Math.pow((cs + 0.055) / 1.055, 2.4);
}

/** linear-light 0–1 → sRGB 0–255 channel. */
function linearToSrgb(c: number): number {
  const cs = c <= 0.0031308 ? c * 12.92 : 1.055 * Math.pow(c, 1 / 2.4) - 0.055;
  return clamp255(cs * 255);
}

// ─── WCAG relative luminance + contrast ──────────────────────────────────────

/** WCAG relative luminance (0–1) of a hex color. Invalid hex → 0. */
export function relativeLuminance(hex: string): number {
  const rgb = parseHex(hex);
  if (!rgb) return 0;
  const r = srgbToLinear(rgb.r);
  const g = srgbToLinear(rgb.g);
  const b = srgbToLinear(rgb.b);
  return 0.2126 * r + 0.7152 * g + 0.0722 * b;
}

/**
 * WCAG contrast ratio between two hex colors (1..21). Order-independent.
 * black/white → 21:1; identical colors → 1:1.
 */
export function contrastRatio(hexA: string, hexB: string): number {
  const la = relativeLuminance(hexA);
  const lb = relativeLuminance(hexB);
  const lighter = Math.max(la, lb);
  const darker = Math.min(la, lb);
  return (lighter + 0.05) / (darker + 0.05);
}

/**
 * Perceived luminance (sRGB-weighted, 0..255) — MUST match the buyer storefront
 * `readableOn` weighting (0.299 R + 0.587 G + 0.114 B). Invalid hex → 0.
 */
export function perceivedLuminance(hex: string): number {
  const rgb = parseHex(hex);
  if (!rgb) return 0;
  return 0.299 * rgb.r + 0.587 * rgb.g + 0.114 * rgb.b;
}

// ─── sRGB ↔ CIE Lab (D65) ────────────────────────────────────────────────────

// D65 reference white.
const XN = 0.95047;
const YN = 1.0;
const ZN = 1.08883;

function labF(t: number): number {
  const d = 6 / 29;
  return t > d * d * d ? Math.cbrt(t) : t / (3 * d * d) + 4 / 29;
}

function labFInv(t: number): number {
  const d = 6 / 29;
  return t > d ? t * t * t : 3 * d * d * (t - 4 / 29);
}

export function rgbToLab({ r, g, b }: Rgb): Lab {
  const rl = srgbToLinear(r);
  const gl = srgbToLinear(g);
  const bl = srgbToLinear(b);
  // linear sRGB → XYZ (D65).
  const x = rl * 0.4124564 + gl * 0.3575761 + bl * 0.1804375;
  const y = rl * 0.2126729 + gl * 0.7151522 + bl * 0.072175;
  const z = rl * 0.0193339 + gl * 0.119192 + bl * 0.9503041;
  const fx = labF(x / XN);
  const fy = labF(y / YN);
  const fz = labF(z / ZN);
  return {
    L: 116 * fy - 16,
    a: 500 * (fx - fy),
    b: 200 * (fy - fz),
  };
}

export function labToRgb({ L, a, b }: Lab): Rgb {
  const fy = (L + 16) / 116;
  const fx = fy + a / 500;
  const fz = fy - b / 200;
  const x = XN * labFInv(fx);
  const y = YN * labFInv(fy);
  const z = ZN * labFInv(fz);
  // XYZ → linear sRGB.
  const rl = x * 3.2404542 + y * -1.5371385 + z * -0.4985314;
  const gl = x * -0.969266 + y * 1.8760108 + z * 0.041556;
  const bl = x * 0.0556434 + y * -0.2040259 + z * 1.0572252;
  return { r: linearToSrgb(rl), g: linearToSrgb(gl), b: linearToSrgb(bl) };
}

export function hexToLab(hex: string): Lab | null {
  const rgb = parseHex(hex);
  return rgb ? rgbToLab(rgb) : null;
}

export function labToHex(lab: Lab): string {
  return formatHex(labToRgb(lab));
}

/** CIE76 ΔE — Euclidean distance in Lab. */
export function deltaE(a: Lab, b: Lab): number {
  const dL = a.L - b.L;
  const da = a.a - b.a;
  const db = a.b - b.b;
  return Math.sqrt(dL * dL + da * da + db * db);
}

/** Convenience: CIE76 ΔE between two hex colors (invalid hex → Infinity). */
export function deltaEHex(hexA: string, hexB: string): number {
  const a = hexToLab(hexA);
  const b = hexToLab(hexB);
  if (!a || !b) return Infinity;
  return deltaE(a, b);
}

// ─── sRGB ↔ OKLab ────────────────────────────────────────────────────────────

export function rgbToOkLab({ r, g, b }: Rgb): OkLab {
  const rl = srgbToLinear(r);
  const gl = srgbToLinear(g);
  const bl = srgbToLinear(b);

  const l = 0.4122214708 * rl + 0.5363325363 * gl + 0.0514459929 * bl;
  const m = 0.2119034982 * rl + 0.6806995451 * gl + 0.1073969566 * bl;
  const s = 0.0883024619 * rl + 0.2817188376 * gl + 0.6299787005 * bl;

  const l_ = Math.cbrt(l);
  const m_ = Math.cbrt(m);
  const s_ = Math.cbrt(s);

  return {
    L: 0.2104542553 * l_ + 0.793617785 * m_ - 0.0040720468 * s_,
    a: 1.9779984951 * l_ - 2.428592205 * m_ + 0.4505937099 * s_,
    b: 0.0259040371 * l_ + 0.7827717662 * m_ - 0.808675766 * s_,
  };
}

export function okLabToRgb({ L, a, b }: OkLab): Rgb {
  const l_ = L + 0.3963377774 * a + 0.2158037573 * b;
  const m_ = L - 0.1055613458 * a - 0.0638541728 * b;
  const s_ = L - 0.0894841775 * a - 1.291485548 * b;

  const l = l_ * l_ * l_;
  const m = m_ * m_ * m_;
  const s = s_ * s_ * s_;

  const rl = 4.0767416621 * l - 3.3077115913 * m + 0.2309699292 * s;
  const gl = -1.2684380046 * l + 2.6097574011 * m - 0.3413193965 * s;
  const bl = -0.0041960863 * l - 0.7034186147 * m + 1.707614701 * s;

  return { r: linearToSrgb(rl), g: linearToSrgb(gl), b: linearToSrgb(bl) };
}

export function hexToOkLab(hex: string): OkLab | null {
  const rgb = parseHex(hex);
  return rgb ? rgbToOkLab(rgb) : null;
}

export function okLabToHex(lab: OkLab): string {
  return formatHex(okLabToRgb(lab));
}

/** OKLab chroma (√(a²+b²)). */
export function okLabChroma({ a, b }: OkLab): number {
  return Math.sqrt(a * a + b * b);
}

/** OKLab hue angle in radians (atan2(b, a)). */
export function okLabHue({ a, b }: OkLab): number {
  return Math.atan2(b, a);
}
